<script lang="ts" setup>
  import { cn } from "@/lib/utils"
  import {
    StepperIndicator,
    useForwardProps,
    type StepperIndicatorProps,
  } from "reka-ui"
  import { computed, type HTMLAttributes } from "vue"
  import Icon from "@/components/ui/icons/Icon.vue"

  import { StepperItemState, StepperItemStateType } from "./StepperTypes"

  const props = defineProps<
    StepperIndicatorProps & {
      class?: HTMLAttributes["class"]
      state: StepperItemStateType
      isAnimated?: boolean
    }
  >()

  const isAnimatingCompletion = computed(
    () => props.isAnimated && props.state === StepperItemState.COMPLETE,
  )

  const indicatorSettings = computed(() => {
    switch (props.state) {
      case StepperItemState.ERROR:
        return {
          background: "bg-red-300",
          stroke: "stroke-secondary-700",
        }
      case StepperItemState.ACTIVE:
        return {
          background: "bg-primary-400",
          stroke: "stroke-primary-600 opacity-60",
        }
      case StepperItemState.COMPLETE:
        return {
          background: "bg-primary-400",
          stroke: "stroke-primary-600",
        }
      default:
        return {
          background: "bg-secondary-300",
          stroke: "stroke-secondary-400",
        }
    }
  })

  const delegatedProps = computed(() => {
    const { class: _, isAnimated: __, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardProps(delegatedProps)
</script>

<template>
  <StepperIndicator
    v-bind="forwarded"
    :class="
      cn(
        'text-muted-foreground/50 relative z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border-4 border-secondary-100',
        indicatorSettings.background,
        isAnimatingCompletion &&
          'transition-colors duration-500 motion-reduce:transition-none',
        props.class,
      )
    "
  >
    <span
      v-if="isAnimatingCompletion"
      class="absolute inset-0 animate-ripple rounded-full bg-primary-400 motion-reduce:hidden"
      data-testid="stepper-indicator-ripple"
      aria-hidden="true"
    />
    <Icon
      :class="
        cn(
          'relative',
          indicatorSettings.stroke,
          isAnimatingCompletion &&
            '[&_path]:animate-tick-draw motion-reduce:[&_path]:animate-none',
        )
      "
      :size="'md'"
      :name="state === StepperItemState.ERROR ? 'Close' : 'Check'"
      :variant="'primary'"
    />
  </StepperIndicator>
</template>
